import { useMemo } from 'react';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { clientsAssetsApi } from '@/api/services/clientsAssets';
import { formatNumber, formatDateTime } from '@/utils/format';
import { ApiError } from '@/api/client/abpError';
import type { ClientsAssetsDetailedInputDto, ClientsAssetsDetailedRowDto } from '@/api/types/clientsAssets';

interface DetailedTabProps {
  referenceDate: string;
  isLive: boolean;
  reloadKey: number;
}

type Extra = { referenceDate: string; isLive: boolean };

const num = (v: number) => formatNumber(v, 2);

export function DetailedTab({ referenceDate, isLive, reloadKey }: DetailedTabProps) {
  const extraParams = useMemo<Extra>(() => ({ referenceDate, isLive }), [referenceDate, isLive]);

  const table = useServerDataTable<ClientsAssetsDetailedRowDto, Extra>({
    queryKey: ['clients-assets', 'detailed', reloadKey],
    fetcher: (req) => clientsAssetsApi.getDetailedReportForDataTable(req as ClientsAssetsDetailedInputDto),
    pageSize: 5,
    extraParams,
  });

  const columns = useMemo<DataGridColumn<ClientsAssetsDetailedRowDto>[]>(
    () => [
      { key: 'uniqueId', title: 'Unique ID', value: (r) => r.uniqueId },
      { key: 'login', title: 'Login', value: (r) => r.login },
      { key: 'registrationDate', title: 'Registration', filterable: false, value: (r) => r.registrationDate, cell: (r) => formatDateTime(r.registrationDate) },
      { key: 'currency', title: 'Acc Currency', value: (r) => r.currency },
      { key: 'mifidClassification', title: 'MiFid Classification', value: (r) => r.mifidClassification },
      // Legacy _DetailedTab does NOT mark Asset Type searchable — match it (filterable:false).
      { key: 'assetTypeLabel', title: 'Asset Type', filterable: false, value: (r) => r.assetTypeLabel },
      { key: 'depositsRc', title: 'Deposits RC', filterable: false, className: 'text-end', value: (r) => r.depositsRc, cell: (r) => num(r.depositsRc) },
      { key: 'withdrawalsRc', title: 'Withdrawals RC', filterable: false, className: 'text-end', value: (r) => r.withdrawalsRc, cell: (r) => num(r.withdrawalsRc) },
      { key: 'profitRc', title: 'Profit RC', filterable: false, className: 'text-end', value: (r) => r.profitRc, cell: (r) => num(r.profitRc) },
      { key: 'swapsRc', title: 'Swaps RC', filterable: false, className: 'text-end', value: (r) => r.swapsRc, cell: (r) => num(r.swapsRc) },
      { key: 'commissionsRc', title: 'Commissions RC', filterable: false, className: 'text-end', value: (r) => r.commissionsRc, cell: (r) => num(r.commissionsRc) },
      { key: 'balanceAc', title: 'Balance AC', filterable: false, className: 'text-end', value: (r) => r.balanceAc, cell: (r) => num(r.balanceAc) },
      { key: 'balanceRc', title: 'Balance RC', filterable: false, className: 'text-end', value: (r) => r.balanceRc, cell: (r) => num(r.balanceRc) },
      { key: 'equityAc', title: 'Equity AC', filterable: false, className: 'text-end', value: (r) => r.equityAc, cell: (r) => num(r.equityAc) },
      { key: 'equityRc', title: 'Equity RC', filterable: false, className: 'text-end', value: (r) => r.equityRc, cell: (r) => num(r.equityRc) },
      { key: 'country', title: 'Country', value: (r) => r.country },
      { key: 'connector', title: 'Connector', value: (r) => r.connector },
    ],
    [],
  );

  const errMsg = table.isError
    ? table.error instanceof ApiError
      ? table.error.message
      : 'Failed to load the detailed report.'
    : null;

  return (
    <div>
      {errMsg && <div className="alert alert-danger mb-2">{errMsg}</div>}
      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => `${r.connector}-${r.login}-${r.assetTypeLabel}`}
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={table.setColumnFilter}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
        emptyMessage={table.isLoading ? 'Loading…' : table.isError ? 'Failed to load.' : 'No data available'}
      />
    </div>
  );
}
